import { createFileRoute, Link } from "@tanstack/react-router";
import { Database, BarChart3, Cloud, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/data")({
  component: DataComingSoonPage,
  head: () => ({
    meta: [
      { title: "Data — AskBell" },
      {
        name: "description",
        content:
          "Connect AskBell to the Bellomy Research Cloud for datasets, crosstabs, and live analysis.",
      },
    ],
  }),
});

function DataComingSoonPage() {
  return (
    <AppLayout>
      <div className="mx-auto max-w-3xl px-6 py-12">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="text-ui-sm uppercase tracking-wider">
            Coming soon
          </Badge>
        </div>
        <h1 className="text-ui-stat font-bold tracking-tight text-foreground">
          Data
        </h1>
        <p className="mt-3 text-ui-lg text-muted-foreground">
          Bring the Bellomy Research Cloud into AskBell. Pull datasets, run
          crosstabs, and ask questions of live study data — without leaving the
          chat.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <FeatureCard
            icon={Cloud}
            title="Connect to Research Cloud"
            description="Securely link your Bellomy projects and datasets so AskBell can read directly from them."
          />
          <FeatureCard
            icon={BarChart3}
            title="Crosstab analysis"
            description="Generate weighted crosstabs, banners, and significance testing through natural language."
          />
          <FeatureCard
            icon={Database}
            title="Live data queries"
            description="Filter, segment, and aggregate study data on demand — no exports required."
          />
          <FeatureCard
            icon={Sparkles}
            title="AI-driven insights"
            description="Pair retrieved data with AskBell's analysis to surface findings and follow-up questions."
          />
        </div>

        <Card className="mt-8 border-dashed">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div>
              <h2 className="text-ui-base font-bold text-foreground">
                We're building this next
              </h2>
              <p className="mt-1 text-ui-base text-muted-foreground">
                In the meantime, upload a CSV or Excel export to the App Builder
                to explore the kind of analysis Data will unlock.
              </p>
            </div>
            <Button asChild variant="outline" className="shrink-0">
              <Link to="/apps">Try App Builder</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ui-md bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-ui-base font-bold text-foreground">{title}</h3>
            <p className="mt-1 text-ui-sm text-muted-foreground leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
